import { useState, type ReactElement } from 'react';
import { Loader2 } from 'lucide-react';
import type {
  AggregatedPackage,
  PackageListState,
} from '@kirin/api-client/contracts/agent-command-events';
import { BlockToggle } from '@/components/shared/BlockToggle';
import { EnforcementSource } from '@/components/shared/EnforcementSource';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import type {
  PackageIdentity,
  PackageListActions,
} from '@/hooks/api/usePackageListActions';
import type { InventoryGroupRuleActions } from '@/hooks/api/useInventoryGroupRuleActions';

/** KIR-4701: the security group whose choices the cell shows and edits. */
export interface PackageGroupScope {
  id: string;
  name: string;
  actions: InventoryGroupRuleActions<PackageIdentity>;
}

/**
 * "Enforcement" cell for the Packages tab (KIR-4443, AC 8 + AC 9): the shared
 * green Allowed / red Blocked switch.
 *
 * Allowed means "not blocked": moving the switch to Allowed removes the
 * package's Block rule, so it is judged by the Malicious Packages detection
 * policy like any other package. The organization has no Allow list. In a
 * security-group scope (KIR-4701) Allowed instead stores the group's own Allow,
 * which lifts the organization's Block for the group's members.
 */

const DESCRIPTIONS: Record<PackageListState, string> = {
  none: 'Not blocked. Installs are permitted and still judged by the Malicious Packages detection policy.',
  blocked: 'Blocked. Coding agents are denied the install before it runs.',
};

/**
 * A rule is name + ecosystem and nothing else, so it covers every version and
 * every package manager that installs from that registry (npm, pnpm, yarn and
 * npx all resolve to `npm`). Said in the confirmation, because an admin
 * blocking one risky release would otherwise reasonably assume they had
 * blocked only that.
 */
function ruleScopeNote(ecosystem: string): string {
  return `Applies to every version, from every package manager that installs from ${ecosystem}.`;
}

/**
 * Package names are often ordinary words or verbs ("is-odd", "request", "run"),
 * so a bare "Block is-odd" does not read as an action on a package.
 */
function packageSubject(name: string): string {
  return `"${name}" package`;
}

interface PackageStateCellProps {
  row: AggregatedPackage;
  actions: PackageListActions;
  /**
   * Whether the viewer may change the lists. Read-only viewers still see the
   * state — it is information, not an action.
   */
  canEdit: boolean;
  group?: PackageGroupScope;
}

export function PackageStateCell({
  row,
  actions,
  canEdit,
  group,
}: PackageStateCellProps): ReactElement {
  const state = row.listState;
  const blocked = state === 'blocked';
  const [confirmingBlock, setConfirmingBlock] = useState(false);
  // The scope the confirmation opened in (group id, or `null` org-wide).
  // Confirm writes only while the live scope still matches it.
  // The dialog's copy comes from it too, so it never describes a write the
  // guard then refuses.
  const [confirmScope, setConfirmScope] = useState<PackageGroupScope | null>(
    null
  );
  const { toast } = useToast();

  // A rule is name + ecosystem. An unmapped manager has no ecosystem, so no
  // rule can name the package and there is nothing to offer.
  const ecosystem = row.ecosystem;
  const name = row.packageName?.trim() ?? '';
  const actionable = canEdit && ecosystem != null && name !== '';

  if (!actionable) {
    const stateLabel = (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'text-xs font-medium',
              blocked ? 'text-red-600' : 'text-green-600'
            )}
            tabIndex={0}
          >
            {blocked ? 'Blocked' : 'Allowed'}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          {DESCRIPTIONS[state]}
        </TooltipContent>
      </Tooltip>
    );
    if (!group) return stateLabel;
    return (
      <div className="flex items-center gap-2">
        {stateLabel}
        <EnforcementSource
          source={row.enforcementSource}
          groupName={group.name}
        />
      </div>
    );
  }

  const pkg = { name, ecosystem };
  const pending = group ? group.actions.isPending(pkg) : actions.isPending(pkg);

  return (
    // The row is click-to-drill-down; without this a click in the
    // confirmation (portaled, but React events still bubble here) would also
    // open the occurrences dialog.
    <div
      className="flex items-center gap-2"
      onClick={event => event.stopPropagation()}
    >
      {/*
        Block is the one move that can stop other people's installs, so it
        alone asks first. Allowed only returns the package to detection.
      */}
      <BlockToggle
        blocked={blocked}
        name={packageSubject(name)}
        onAllow={() =>
          void (group
            ? group.actions.setChoice(pkg, 'allow')
            : actions.clear(pkg))
        }
        onRequestBlock={() => {
          setConfirmScope(group ?? null);
          setConfirmingBlock(true);
        }}
        disabled={pending}
        switchClassName="data-[state=checked]:bg-green-600"
      />
      {pending && (
        <Loader2
          className="size-3 shrink-0 animate-spin text-muted-foreground"
          aria-label="Updating package lists"
        />
      )}
      {group && (
        <EnforcementSource
          source={row.enforcementSource}
          groupName={group.name}
        />
      )}
      <ConfirmationDialog
        isOpen={confirmingBlock}
        onClose={() => setConfirmingBlock(false)}
        onConfirm={() => {
          setConfirmingBlock(false);
          // The group was deleted, or the scope changed, while the dialog
          // was open: the write would not land where the dialog said.
          if ((group?.id ?? null) !== (confirmScope?.id ?? null)) {
            toast({
              title: `${packageSubject(name)} was not blocked`,
              description:
                'The Allow/Block scope changed while the dialog was open. Check the scope and try again.',
              variant: 'destructive',
            });
            return;
          }
          void (group
            ? group.actions.setChoice(pkg, 'block')
            : actions.block(pkg));
        }}
        title={`Block ${packageSubject(name)}?`}
        description={
          `${
            confirmScope
              ? `This applies only to members of ${confirmScope.name}; everyone else keeps the organization's choice. `
              : actions.scope === 'organization'
                ? 'This applies to everyone in this organization. '
                : ''
          }${ruleScopeNote(ecosystem)} Coding agents are denied the ` +
          'install before it runs.'
        }
        confirmLabel="Block"
      />
    </div>
  );
}
